import { unzipSync, strFromU8 } from 'fflate';
import { latLonToUtm, utmCrs, utmToLatLon } from './utm.js';
import { worldReference as makeWorldReference } from './world-coordinates.js';

const KML_COLOR = [52, 210, 183];
const SHP_COLOR = [80, 174, 255];
const SHP_RECORD_TYPES = new Set([0, 1, 3, 5, 8, 11, 13, 15, 18, 21, 23, 25, 28, 31]);

function projectLonLat(lon, lat, z = 0, targetCrs = null) {
  const utm = latLonToUtm(lat, lon, targetCrs?.zone, targetCrs?.hemisphere);
  return { x: utm.easting, y: utm.northing, z: Number.isFinite(z) ? z : 0 };
}

function parseKmlCoordinates(text, targetCrs) {
  return text.trim().split(/\s+/).map(token => {
    const [lon, lat, z = 0] = token.split(',').map(Number);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return projectLonLat(lon, lat, z, targetCrs);
  }).filter(Boolean);
}

function xmlChildren(node, localName) {
  return [...node.getElementsByTagNameNS('*', localName)];
}

function pathLength(points) {
  let length = 0;
  for (let index = 1; index < points.length; index++) length += Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
  return length;
}

function boundsForPoints(points) {
  return points.reduce((value, point) => ({
    minX: Math.min(value.minX, point.x),
    minY: Math.min(value.minY, point.y),
    minZ: Math.min(value.minZ, point.z || 0),
    maxX: Math.max(value.maxX, point.x),
    maxY: Math.max(value.maxY, point.y),
    maxZ: Math.max(value.maxZ, point.z || 0)
  }), {
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity
  });
}

function buildResult(name, paths, points, color, warnings = []) {
  const all = [...points, ...paths.flatMap(path => path.points)];
  if (!all.length) throw new Error(`${name}: no readable geometry was found.`);
  const bounds = boundsForPoints(all);
  const origin = { x: Math.floor(((bounds.minX + bounds.maxX) / 2) / 1000) * 1000, y: Math.floor(((bounds.minY + bounds.maxY) / 2) / 1000) * 1000 };
  const segmentCount = paths.reduce((sum, path) => sum + Math.max(0, path.points.length - 1), 0);
  const linePositions = new Float32Array(segmentCount * 6), lineColors = new Uint8Array(segmentCount * 6), features = [];
  let segment = 0;
  for (const path of paths) {
    const start = segment;
    for (let index = 1; index < path.points.length; index++, segment++) {
      const a = path.points[index - 1], b = path.points[index], offset = segment * 6;
      linePositions.set([a.x - origin.x, a.y - origin.y, a.z || 0, b.x - origin.x, b.y - origin.y, b.z || 0], offset);
      lineColors.set([...color, ...color], offset);
    }
    const count = segment - start;
    if (count) features.push({ start, count, layer: path.layer || 'IMPORT', type: path.type || 'POLYLINE', closed: !!path.closed, length2d: pathLength(path.points) });
  }
  const pointPositions = new Float32Array(points.length * 3), pointColors = new Uint8Array(points.length * 3);
  points.forEach((point, index) => {
    pointPositions.set([point.x - origin.x, point.y - origin.y, point.z || 0], index * 3);
    pointColors.set(color, index * 3);
  });
  return { name, origin, bounds, linePositions, lineColors, pointPositions, pointColors, texts: [], features, segments: segmentCount, colorCount: 1, surface: null, warnings };
}

function kmlLonLatBounds(document) {
  const bounds = { minX: Infinity, minY: Infinity, minZ: 0, maxX: -Infinity, maxY: -Infinity, maxZ: 0 };
  for (const node of xmlChildren(document, 'coordinates')) {
    for (const token of node.textContent.trim().split(/\s+/)) {
      const [lon, lat] = token.split(',').map(Number);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      bounds.minX = Math.min(bounds.minX, lon);
      bounds.minY = Math.min(bounds.minY, lat);
      bounds.maxX = Math.max(bounds.maxX, lon);
      bounds.maxY = Math.max(bounds.maxY, lat);
    }
  }
  return Number.isFinite(bounds.minX) ? bounds : null;
}

function parseKmlText(text, name, requestedTargetCrs = null) {
  const document = new DOMParser().parseFromString(text, 'application/xml');
  const parserError = document.querySelector('parsererror');
  if (parserError) throw new Error(`${name}: invalid KML structure.`);
  const sourceBounds = kmlLonLatBounds(document);
  if(!sourceBounds)throw new Error(`${name}: no readable WGS 84 coordinates were found.`);
  const centerLon = (sourceBounds.minX + sourceBounds.maxX) / 2;
  const centerLat = (sourceBounds.minY + sourceBounds.maxY) / 2;
  const natural=latLonToUtm(centerLat,centerLon);
  const detectedCrs=utmCrs(natural.zone,natural.hemisphere);
  const targetCrs=requestedTargetCrs?.type==='utm'?utmCrs(requestedTargetCrs.zone,requestedTargetCrs.hemisphere):detectedCrs;
  const paths = [], points = [];
  const placemarks = xmlChildren(document, 'Placemark');
  for (const placemark of placemarks) {
    const label = xmlChildren(placemark, 'name')[0]?.textContent?.trim() || 'KML';
    for (const node of xmlChildren(placemark, 'Point')) {
      const coordinate = xmlChildren(node, 'coordinates')[0];
      if (coordinate) points.push(...parseKmlCoordinates(coordinate.textContent,targetCrs));
    }
    for (const node of xmlChildren(placemark, 'LineString')) {
      const coordinate = xmlChildren(node, 'coordinates')[0], line = coordinate ? parseKmlCoordinates(coordinate.textContent,targetCrs) : [];
      if (line.length > 1) paths.push({ points: line, layer: label, type: 'KML_LINE', closed: false });
    }
    for (const node of xmlChildren(placemark, 'Polygon')) {
      const rings = xmlChildren(node, 'LinearRing');
      for (const ring of rings) {
        const coordinate = xmlChildren(ring, 'coordinates')[0], polygon = coordinate ? parseKmlCoordinates(coordinate.textContent,targetCrs) : [];
        if (polygon.length > 2) {
          const first = polygon[0], last = polygon.at(-1);
          if (first.x !== last.x || first.y !== last.y) polygon.push({ ...first });
          paths.push({ points: polygon, layer: label, type: 'KML_POLYGON', closed: true });
        }
      }
    }
  }
  const result=buildResult(name, paths, points, KML_COLOR, [`KML WGS 84 coordinates were converted to ${targetCrs.name}.`]);
  return Object.assign(result, {
    sourceCrs: {
      type: 'geographic',
      datum: 'WGS84',
      epsg: 4326,
      name: 'WGS 84',
      unit: 'degree',
      status: 'resolved'
    },
    sourceCrsStatus: 'resolved',
    sourceBounds,
    worldReference: makeWorldReference(centerLat, centerLon),
    crsEvidence: 'kml-wgs84-standard',
    detectedCrs,
    projectCrs: targetCrs,
    crsAction: 'reproject'
  });
}

export function parseKml(buffer, name, targetCrs = null) {
  return parseKmlText(new TextDecoder().decode(buffer), name, targetCrs);
}

export function parseKmz(buffer, name, targetCrs = null) {
  const archive = unzipSync(new Uint8Array(buffer));
  const entry = Object.keys(archive).find(key => key.toLowerCase().endsWith('.kml'));
  if (!entry) throw new Error(`${name}: the KMZ archive does not contain a KML file.`);
  return parseKmlText(strFromU8(archive[entry]), name, targetCrs);
}

function shpZValues(view, type, count, xyEnd, contentEnd) {
  if (![11, 13, 15, 18, 31].includes(type)) return null;
  const start = xyEnd + 16;
  if (start + count * 8 > contentEnd) return null;
  return Array.from({ length: count }, (_, index) => view.getFloat64(start + index * 8, true));
}

export function parsePrjCrs(prjText) {
  const text = String(prjText || '').trim();
  if (!text) return null;

  const epsgCodes = [
    ...text.matchAll(/(?:AUTHORITY|ID)\s*\[\s*["']EPSG["']\s*,\s*["']?(\d+)["']?/gi)
  ].map(match => Number(match[1]));

  const utmEpsg = epsgCodes.find(code =>
    (code >= 32601 && code <= 32660) ||
    (code >= 32701 && code <= 32760)
  );

  if (utmEpsg) {
    const hemisphere = utmEpsg < 32700 ? 'N' : 'S';
    const zone = utmEpsg % 100;
    return {
      kind: 'utm',
      crs: utmCrs(zone, hemisphere)
    };
  }

  const isWgs84 = /WGS[_\s-]*(?:19)?84|WGS[_\s-]*1984/i.test(text);
  const utmName = text.match(/UTM[_\s/]*(?:ZONE[_\s]*)?(\d{1,2})\s*([NS])\b/i);

  if (isWgs84 && utmName) {
    return {
      kind: 'utm',
      crs: utmCrs(Number(utmName[1]), utmName[2])
    };
  }

  const isProjected = /PROJCS|PROJCRS/i.test(text);

  if (
    !isProjected &&
    (
      epsgCodes.includes(4326) ||
      (isWgs84 && /GEOGCS|GEOGCRS/i.test(text))
    )
  ) {
    return {
      kind: 'geographic',
      crs: {
        type: 'geographic',
        datum: 'WGS84',
        epsg: 4326,
        name: 'WGS 84',
        unit: 'degree',
        status: 'resolved'
      }
    };
  }

  throw new Error('The .prj CRS is not supported. Maply currently supports WGS 84 geographic and WGS 84 / UTM .prj files.');
}

function reprojectUtmPoint(point, sourceCrs, targetCrs) {
  if (sourceCrs.epsg === targetCrs.epsg) return { ...point };
  const geographic = utmToLatLon(
    point.x,
    point.y,
    sourceCrs.zone,
    sourceCrs.hemisphere
  );
  return projectLonLat(
    geographic.longitude,
    geographic.latitude,
    point.z,
    targetCrs
  );
}

function validShpRecordHeader(view, offset, expectedRecordNumber, headerType) {
  if (offset + 12 > view.byteLength) return false;
  const recordNumber = view.getInt32(offset, false);
  const contentBytes = view.getInt32(offset + 4, false) * 2;
  const recordType = view.getInt32(offset + 8, true);
  return recordNumber === expectedRecordNumber &&
    contentBytes >= 4 &&
    offset + 8 + contentBytes <= view.byteLength &&
    SHP_RECORD_TYPES.has(recordType) &&
    (recordType === 0 || recordType === headerType);
}

function nextShpRecordOffset(view, offset, expectedRecordNumber, headerType) {
  for (let candidate = offset + 1; candidate + 12 <= view.byteLength; candidate++) {
    if (validShpRecordHeader(view, candidate, expectedRecordNumber, headerType)) {
      return candidate;
    }
  }
  return -1;
}

export function parseShp(buffer, name, requestedTargetCrs = null, prjText = null) {
  const view = new DataView(buffer);
  if (view.byteLength < 100 || view.getInt32(0, false) !== 9994) throw new Error(`${name}: invalid SHP header.`);
  const rawPaths = [], rawPoints = [];
  const headerType = view.getInt32(32, true);
  let offset = 100, recordIndex = 0, recoveredGaps = 0, recoveredBytes = 0;
  while (offset + 12 <= view.byteLength) {
    const expectedRecordNumber = recordIndex + 1;

    if (!validShpRecordHeader(view, offset, expectedRecordNumber, headerType)) {
      const recoveredOffset = nextShpRecordOffset(
        view,
        offset,
        expectedRecordNumber,
        headerType
      );

      if (recoveredOffset < 0) break;
      recoveredGaps++;
      recoveredBytes += recoveredOffset - offset;
      offset = recoveredOffset;
    }

    const contentBytes = view.getInt32(offset + 4, false) * 2, content = offset + 8;
    const type = view.getInt32(content, true); recordIndex++;
    if ([1, 11, 21].includes(type) && contentBytes >= 20) {
      rawPoints.push({ x: view.getFloat64(content + 4, true), y: view.getFloat64(content + 12, true), z: type === 11 && contentBytes >= 28 ? view.getFloat64(content + 20, true) : 0 });
    } else if ([3, 5, 13, 15, 23, 25, 31].includes(type) && contentBytes >= 44) {
      const partCount = view.getInt32(content + 36, true), pointCount = view.getInt32(content + 40, true), partsOffset = content + 44, partTypesOffset = partsOffset + partCount * 4, pointsOffset = type === 31 ? partTypesOffset + partCount * 4 : partTypesOffset;
      if (partCount >= 0 && pointCount >= 0 && pointsOffset + pointCount * 16 <= content + contentBytes) {
        const zValues = shpZValues(view, type, pointCount, pointsOffset + pointCount * 16, content + contentBytes);
        const allPoints = Array.from({ length: pointCount }, (_, index) => ({ x: view.getFloat64(pointsOffset + index * 16, true), y: view.getFloat64(pointsOffset + index * 16 + 8, true), z: zValues?.[index] || 0 }));
        for (let part = 0; part < partCount; part++) {
          const start = view.getInt32(partsOffset + part * 4, true), end = part + 1 < partCount ? view.getInt32(partsOffset + (part + 1) * 4, true) : pointCount;
          const points = allPoints.slice(start, end);

          if (type === 31) {
            const partType = view.getInt32(partTypesOffset + part * 4, true);

            if (partType === 0) {
              for (let index = 0; index + 2 < points.length; index++) {
                const triangle = [
                  points[index],
                  points[index + 1],
                  points[index + 2],
                  points[index]
                ];
                rawPaths.push({ points: triangle, layer: `SHP ${recordIndex}`, type: 'SHP_MULTIPATCH', closed: true });
              }
            } else if (partType === 1) {
              for (let index = 1; index + 1 < points.length; index++) {
                const triangle = [
                  points[0],
                  points[index],
                  points[index + 1],
                  points[0]
                ];
                rawPaths.push({ points: triangle, layer: `SHP ${recordIndex}`, type: 'SHP_MULTIPATCH', closed: true });
              }
            } else if (points.length > 1) {
              const ring = [...points];
              const first = ring[0], last = ring.at(-1);
              if (first.x !== last.x || first.y !== last.y || first.z !== last.z) ring.push({ ...first });
              rawPaths.push({ points: ring, layer: `SHP ${recordIndex}`, type: 'SHP_MULTIPATCH', closed: true });
            }
          } else {
            const closed = [5, 15, 25].includes(type);
            if (points.length > 1) rawPaths.push({ points, layer: `SHP ${recordIndex}`, type: closed ? 'SHP_POLYGON' : 'SHP_POLYLINE', closed });
          }
        }
      }
    } else if ([8, 18, 28].includes(type) && contentBytes >= 40) {
      const pointCount = view.getInt32(content + 36, true), pointsOffset = content + 40;
      if (pointCount >= 0 && pointsOffset + pointCount * 16 <= content + contentBytes) {
        const zValues = shpZValues(view, type, pointCount, pointsOffset + pointCount * 16, content + contentBytes);
        for (let index = 0; index < pointCount; index++) rawPoints.push({ x: view.getFloat64(pointsOffset + index * 16, true), y: view.getFloat64(pointsOffset + index * 16 + 8, true), z: zValues?.[index] || 0 });
      }
    }
    offset = content + contentBytes;
  }
  const sample = rawPoints[0] || rawPaths[0]?.points[0];
  if (!sample) {
    throw new Error(`${name}: no readable SHP geometry was found (shape type ${headerType}).`);
  }
  const sourcePoints = [
    ...rawPoints,
    ...rawPaths.flatMap(path => path.points)
  ];
  const sourceBounds = boundsForPoints(sourcePoints);
  let sourceDefinition = parsePrjCrs(prjText);
  let crsEvidence = sourceDefinition ? 'sidecar-prj' : null;
  const centerX = (sourceBounds.minX + sourceBounds.maxX) / 2;
  const centerY = (sourceBounds.minY + sourceBounds.maxY) / 2;
  const sourceSpanX = sourceBounds.maxX - sourceBounds.minX;
  const sourceSpanY = sourceBounds.maxY - sourceBounds.minY;
  const geographicRange =
    sourceBounds.minX >= -180 && sourceBounds.maxX <= 180 &&
    sourceBounds.minY >= -90 && sourceBounds.maxY <= 90;
  const geographicEvidence =
    geographicRange &&
    Number.isFinite(centerX) && Number.isFinite(centerY) &&
    (Math.abs(centerX) >= 30 || sourceSpanX >= 30) &&
    (sourceSpanX >= 0.001 || sourceSpanY >= 0.001);

  if (!sourceDefinition && geographicEvidence) {
    sourceDefinition = {
      kind: 'geographic',
      crs: {
        type: 'geographic', datum: 'WGS84', epsg: 4326,
        name: 'WGS 84', unit: 'degree', status: 'resolved'
      }
    };
    crsEvidence = 'coordinate-range';
  }
  const requested = requestedTargetCrs?.type === 'utm'
    ? utmCrs(requestedTargetCrs.zone, requestedTargetCrs.hemisphere)
    : null;
  let targetCrs = null;
  let detectedCrs = null;
  let geographicCenter = null;
  let convert = point => ({ ...point });

  if (sourceDefinition?.kind === 'geographic') {
    geographicCenter = { latitude: centerY, longitude: centerX };
    const natural = latLonToUtm(centerY, centerX);
    detectedCrs = utmCrs(natural.zone, natural.hemisphere);
    targetCrs = requested || detectedCrs;
    convert = point => projectLonLat(point.x, point.y, point.z, targetCrs);
  } else if (sourceDefinition?.kind === 'utm') {
    geographicCenter = utmToLatLon(
      centerX,
      centerY,
      sourceDefinition.crs.zone,
      sourceDefinition.crs.hemisphere
    );
    detectedCrs = sourceDefinition.crs;
    targetCrs = requested || sourceDefinition.crs;
    convert = point => reprojectUtmPoint(point, sourceDefinition.crs, targetCrs);
  }

  const paths = rawPaths.map(path => ({ ...path, points: path.points.map(convert) })), points = rawPoints.map(convert);
  const warning = sourceDefinition
    ? crsEvidence === 'coordinate-range'
      ? `SHP coordinates were auto-detected as WGS 84 and converted to ${targetCrs.name}. Use Advanced if the source is actually a local grid.`
      : `SHP ${sourceDefinition.crs.name} coordinates were ${sourceDefinition.crs.epsg === targetCrs.epsg ? 'assigned' : `converted to ${targetCrs.name}`}.`
    : 'SHP has no matching .prj file. Its numeric coordinates are shown unchanged; location is not yet verified.';
  const warnings = [warning];
  if (recoveredGaps) {
    warnings.push(
      `Recovered ${recoveredGaps} SHP record gap${recoveredGaps === 1 ? '' : 's'} (${recoveredBytes} padding bytes).`
    );
  }
  const result=buildResult(name, paths, points, SHP_COLOR, warnings);
  return Object.assign(result, {
    recordCount: recordIndex,
    recoveredGaps,
    recoveredBytes,
    sourceBounds,
    worldReference: geographicCenter
      ? makeWorldReference(geographicCenter.latitude, geographicCenter.longitude)
      : null,
    sourceCrs: sourceDefinition?.crs || null,
    sourceCrsStatus: sourceDefinition ? 'resolved' : 'unknown',
    crsEvidence,
    detectedCrs,
    projectCrs: targetCrs,
    crsAction: !sourceDefinition
      ? 'keep-raw'
      : sourceDefinition.crs.epsg === targetCrs.epsg
        ? 'assign'
        : 'reproject'
  });
}
